import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Skeleton, Typography, useTheme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import PageContainer from '../../components/PageContainer';
import { EmptyState, FilterBar, PageHeader, Panel, StatusChip, useFilterBarStyles } from '../../components/ui';
import PrintFileJobsTable from '../../components/PrintFileJobsTable';
import { PrintFileJobs } from '../../store/slices';
import { IStore } from '../../store/types';
import { getCurrentJob, IPrintFileJobsQueryParams, PRINT_FILE_JOBS_LIMIT_OPTIONS } from '../../api/printFileJobs';
import { JOB_STATUS, JOB_STATUS_ORDER, PRINT_FILE_STATUS } from '../../lib/printFileJobs/ui';

const SORT_OPTIONS: { value: 'DESC' | 'ASC'; label: string }[] = [
    { value: 'DESC', label: 'Newest line items first' },
    { value: 'ASC', label: 'Oldest line items first' },
];

const PrintFileJobsPage = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const filterClasses = useFilterBarStyles();
    const { list, status, error, queryParams } = useSelector<IStore, IStore['printFileJobs']>((state) => state.printFileJobs);
    const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

    const currentParams: IPrintFileJobsQueryParams = { ...PrintFileJobs.DEFAULT_PRINT_FILE_JOBS_PARAMS(), ...queryParams };
    const loading = status === 'loading';

    const loadItems = (params: IPrintFileJobsQueryParams) => {
        dispatch<any>(PrintFileJobs.getPrintFileJobs(params));
    };

    useEffect(() => {
        loadItems({ ...currentParams, page: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = (id: string) => setExpandedIds((s) => ({ ...s, [id]: !s[id] }));
    const expandAll = () => setExpandedIds(Object.fromEntries(list.map((i) => [i.lineItemId, true])));
    const collapseAll = () => setExpandedIds({});
    const allExpanded = list.length > 0 && list.every((i) => expandedIds[i.lineItemId]);

    const summary = useMemo(() => {
        const jobs: Record<string, number> = {};
        const files: Record<string, number> = {};
        let noJobs = 0;
        list.forEach((item) => {
            const cur = getCurrentJob(item);
            if (cur) jobs[cur.status] = (jobs[cur.status] || 0) + 1;
            else noJobs += 1;
            const st = item.printFile?.printFileStatus || 'unknown';
            files[st] = (files[st] || 0) + 1;
        });
        return { jobs, files, noJobs };
    }, [list]);

    const total = Number(currentParams.total) || 0;

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Production"
                title="Print file jobs"
                subtitle="Line items that have a print file, with the print file record and the full job history. Expand a row for every field and all images."
                badges={total > 0 ? <StatusChip tone="neutral">{total.toLocaleString('en-US')} line items</StatusChip> : undefined}
                actions={(
                    <>
                        <Button size="small" variant="outlined" startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />} disabled={loading || list.length === 0} onClick={allExpanded ? collapseAll : expandAll}>
                            {allExpanded ? 'Collapse all' : 'Expand all'}
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} disabled={loading} onClick={() => loadItems(currentParams)}>
                            Refresh
                        </Button>
                    </>
                )}
            />
            <Panel padding="none">
                <FilterBar label="Print file job filters">
                    <FormControl size="small" className={filterClasses.select} sx={{ minWidth: 220 }}>
                        <InputLabel id="print-jobs-sort-label">Sort</InputLabel>
                        <Select
                            labelId="print-jobs-sort-label"
                            label="Sort"
                            value={currentParams.sortDirection || 'DESC'}
                            disabled={loading}
                            onChange={(e) => loadItems({ ...currentParams, page: 1, sortDirection: e.target.value as 'ASC' | 'DESC' })}
                        >
                            {SORT_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" className={filterClasses.select} sx={{ width: 120 }}>
                        <InputLabel id="print-jobs-limit-label">Per page</InputLabel>
                        <Select
                            labelId="print-jobs-limit-label"
                            label="Per page"
                            value={currentParams.limit || 20}
                            disabled={loading}
                            onChange={(e) => loadItems({ ...currentParams, page: 1, limit: +e.target.value })}
                        >
                            {PRINT_FILE_JOBS_LIMIT_OPTIONS.map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {list.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', ml: { md: 'auto' } }}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 0.5 }}>
                                Current jobs on this page:
                            </Typography>
                            {JOB_STATUS_ORDER.filter((s) => summary.jobs[s]).map((s) => (
                                <Chip
                                    key={s}
                                    size="small"
                                    label={`${JOB_STATUS[s].label} ${summary.jobs[s]}`}
                                    sx={{ bgcolor: JOB_STATUS[s].color, color: JOB_STATUS[s].textColor || theme.custom.color.primary.contrast, fontWeight: 600 }}
                                />
                            ))}
                            {summary.noJobs > 0 && <Chip size="small" label={`No jobs ${summary.noJobs}`} />}
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 1, mr: 0.5 }}>
                                Print files:
                            </Typography>
                            {Object.keys(PRINT_FILE_STATUS).filter((s) => summary.files[s]).map((s) => (
                                <Chip
                                    key={s}
                                    size="small"
                                    variant="outlined"
                                    label={`${PRINT_FILE_STATUS[s].label} ${summary.files[s]}`}
                                    sx={{ borderColor: PRINT_FILE_STATUS[s].color, fontWeight: 600 }}
                                />
                            ))}
                        </Box>
                    )}
                </FilterBar>
                {loading && <Skeleton animation="wave" width="100%" height={480} variant="rectangular" />}
                {status === 'resolved' && list.length === 0 && (
                    <EmptyState title="No line items with print files" hint="Nothing matches the current sort and page size." />
                )}
                {status === 'rejected' && (
                    <EmptyState title="The jobs could not be loaded" hint={error || undefined} action={<Button variant="outlined" onClick={() => loadItems(currentParams)}>Retry</Button>} />
                )}
            </Panel>
            {!loading && list.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <PrintFileJobsTable
                        items={list}
                        paging={currentParams}
                        pagingUpdate={loadItems}
                        expandedIds={expandedIds}
                        onToggle={toggle}
                    />
                </Box>
            )}
        </PageContainer>
    );
};

export default PrintFileJobsPage;
